import {
  type ColumnVisibilityState,
  columnFilteringFeature,
  columnOrderingFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  createTableHook,
  filterFn_arrIncludes,
  filterFn_equals,
  filterFn_includesString,
  filterFn_inDateRange,
  filterFn_inNumberRange,
  filterFn_weakEquals,
  metaHelper,
  type ReactTable,
  type RowData,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  type Column as TanStackColumn,
  type Header as TanStackHeader,
  type Row as TanStackRow,
  tableFeatures,
} from '@tanstack/react-table';

type EmmaColumnMeta = {
  isSticky?: boolean;
  filterVariant?:
    | { type: 'dateRange' }
    | {
        type: 'multiSearch' | 'singleSearch';
        constraint?: RegExp;
      }
    | {
        type: 'singleSelect' | 'multiSelect';
        options: { name: string; filterValue: unknown }[];
      };
};

export const emmaTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnOrderingFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  columnMeta: metaHelper<EmmaColumnMeta>(),
  expandedRowModel: createExpandedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: {
    arrIncludes: filterFn_arrIncludes,
    equals: filterFn_equals,
    inDateRange: filterFn_inDateRange,
    inNumberRange: filterFn_inNumberRange,
    includesString: filterFn_includesString,
    weakEquals: filterFn_weakEquals,
  },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

export const { createAppColumnHelper: createColumnHelper, useAppTable } = createTableHook({
  enableRowRangeSelection: false,
  features: emmaTableFeatures,
});

export type ColumnDef<TData extends RowData> = ReturnType<
  ReturnType<typeof createColumnHelper<TData>>['columns']
>[number];
export type Column<TData extends RowData> = TanStackColumn<
  typeof emmaTableFeatures,
  TData,
  unknown
>;
export type Header<TData extends RowData> = TanStackHeader<typeof emmaTableFeatures, TData>;
export type Row<TData extends RowData> = TanStackRow<typeof emmaTableFeatures, TData>;
export type Table<TData extends RowData> = ReactTable<typeof emmaTableFeatures, TData>;
export type VisibilityState = ColumnVisibilityState;
